/**
 * Mode `popup` du SDK widget Rintintin (Phase 3).
 *
 * Ouvre un overlay modal centré au clic sur un trigger (sélecteur CSS fourni
 * ou bouton auto-injecté). L'iframe est créée paresseusement à la première
 * ouverture pour ne pas charger inutilement le widget si l'utilisateur ne
 * clique jamais.
 *
 * A11y :
 *  - overlay = role="dialog" + aria-modal="true"
 *  - focus déplacé sur le bouton close à l'ouverture, restauré à la fermeture
 *  - focus trap (Tab/Shift+Tab boucle dans le cadre)
 *  - ESC ferme
 */

import { applyTriggerAccentStyles } from './colors';
import { attachCheckoutFrameListener } from './checkoutFrame';
import { mergeCheckoutCompleteCallback } from './callbackHelpers';
import { buildWidgetEmbedUrl, normalizeBaseUrl, DEFAULT_WIDGET_BASE_URL, withSdkHostContext } from './url';
import { attachResizeListener, resolveBaseOrigin } from './resize';
import {
  createEventDispatcher,
  resolveAllowedOrigins,
  type EventDispatcherHandle,
} from './dispatcher';
import { hostPrefersDutch } from './hostLocale';
import { ensureStylesInjected } from './styles';
import type { RintintinBookingInitOptions, RintintinBookingInstance } from './types';

const LOG_PREFIX = '[RintintinBooking]';

/** Hauteur initiale de l'iframe popup (px) — le resize message ajustera ensuite. */
const POPUP_INITIAL_HEIGHT_PX = 600;

/** Sélecteur des éléments focusables, utilisé pour le focus trap. */
const FOCUSABLE_SELECTOR =
  'button, [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Résout l'élément hôte d'un bouton auto-injecté. Préfère :
 *  1. `opts.container` si fourni (HTMLElement direct ou sélecteur CSS),
 *  2. sinon `document.body`.
 */
function resolveTriggerHost(opts: RintintinBookingInitOptions): HTMLElement {
  if (opts.container) {
    if (typeof opts.container !== 'string') return opts.container;
    try {
      const el = document.querySelector<HTMLElement>(opts.container);
      if (el) return el;
    } catch {
      /* sélecteur invalide → fallback body */
    }
  }
  return document.body;
}

/**
 * Crée la popup widget. Lazy mount de l'iframe : tant que `open()` n'a pas été
 * appelé, l'iframe n'existe pas dans le DOM.
 */
export function mountPopupWidget(options: RintintinBookingInitOptions): RintintinBookingInstance {
  ensureStylesInjected(document);

  const embedUrl = buildWidgetEmbedUrl(withSdkHostContext(options));
  const normalizedBase = normalizeBaseUrl(options.baseUrl || DEFAULT_WIDGET_BASE_URL);
  const baseOrigin = resolveBaseOrigin(normalizedBase);
  const allowedOrigins = resolveAllowedOrigins(options.allowedOrigins, normalizedBase);

  // Overlay + cadre + bouton close : créés tout de suite (légers), iframe lazy
  const overlay = document.createElement('div');
  overlay.className = 'rtt-rb-popup-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');

  const frame = document.createElement('div');
  frame.className = 'rtt-rb-popup-frame';

  const dutchHost = hostPrefersDutch();
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'rtt-rb-popup-close';
  closeBtn.setAttribute('aria-label', dutchHost ? 'Sluiten' : 'Fermer');
  closeBtn.innerHTML = '&times;';

  frame.appendChild(closeBtn);
  overlay.appendChild(frame);
  document.body.appendChild(overlay);

  // État
  let iframe: HTMLIFrameElement | null = null;
  let detachResize: (() => void) | null = null;
  let detachCheckoutFrame: (() => void) | null = null;
  let dispatcher: EventDispatcherHandle | null = null;
  let injectedTrigger: HTMLButtonElement | null = null;
  let externalTrigger: { el: Element; handler: (e: Event) => void } | null = null;
  let previousFocus: HTMLElement | null = null;
  let isOpen = false;
  let destroyed = false;

  /** Crée l'iframe au premier `open()` (lazy mount). Idempotent. */
  function ensureIframe(): void {
    if (iframe) return;
    iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.className = 'rtt-rb-popup-iframe';
    iframe.title = dutchHost ? 'Boeken — Rintintin' : 'Réserver — Rintintin';
    iframe.setAttribute('width', '100%');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.style.height = `${POPUP_INITIAL_HEIGHT_PX}px`;
    frame.appendChild(iframe);
    detachResize = attachResizeListener(iframe, baseOrigin);
    detachCheckoutFrame = attachCheckoutFrameListener(iframe, frame);
    dispatcher = createEventDispatcher({
      iframe,
      allowedOrigins,
      callbacks: mergeCheckoutCompleteCallback(options, () => {
        frame.classList.remove('rtt-rb-popup-frame--checkout');
        close();
      }),
    });
  }

  /** Récupère les éléments focusables actuellement dans le cadre. */
  function getFocusable(): HTMLElement[] {
    return Array.from(frame.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      el => !el.hasAttribute('disabled')
    );
  }

  /** Gestion clavier : ESC ferme, Tab/Shift+Tab boucle dans le cadre. */
  function onKeyDown(event: KeyboardEvent): void {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (active === first || !frame.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last || !frame.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /** Clic sur l'overlay (mais pas sur le cadre) → ferme la popup. */
  function onOverlayClick(event: MouseEvent): void {
    if (event.target === overlay) {
      close();
    }
  }

  function open(): void {
    if (destroyed || isOpen) return;
    ensureIframe();
    previousFocus = (document.activeElement as HTMLElement) || null;
    overlay.classList.add('rtt-rb-popup-overlay--open');
    isOpen = true;
    // Focus immédiat sur le bouton close (l'iframe charge en arrière-plan)
    closeBtn.focus();
    document.addEventListener('keydown', onKeyDown);
    overlay.addEventListener('click', onOverlayClick);
  }

  function close(): void {
    if (!isOpen) return;
    overlay.classList.remove('rtt-rb-popup-overlay--open');
    frame.classList.remove('rtt-rb-popup-frame--checkout');
    isOpen = false;
    document.removeEventListener('keydown', onKeyDown);
    overlay.removeEventListener('click', onOverlayClick);
    // Restaure le focus précédent si toujours dans le DOM
    if (previousFocus && document.contains(previousFocus)) {
      try {
        previousFocus.focus();
      } catch {
        /* certains éléments ne sont plus focusables */
      }
    }
    previousFocus = null;
  }

  closeBtn.addEventListener('click', close);

  // --- Trigger : sélecteur fourni OU bouton auto-injecté ---
  if (options.trigger) {
    let el: Element | null = null;
    try {
      el = document.querySelector(options.trigger);
    } catch {
      console.warn(`${LOG_PREFIX} sélecteur trigger invalide :`, options.trigger);
    }
    if (!el) {
      console.warn(
        `${LOG_PREFIX} élément trigger introuvable :`,
        options.trigger,
        '— utilisez instance.open() programmatiquement ou triggerLabel.'
      );
    } else {
      const handler = (e: Event) => {
        e.preventDefault();
        open();
      };
      el.addEventListener('click', handler);
      externalTrigger = { el, handler };
    }
  } else if (options.triggerLabel) {
    injectedTrigger = document.createElement('button');
    injectedTrigger.type = 'button';
    injectedTrigger.className = 'rtt-rb-trigger';
    injectedTrigger.textContent = options.triggerLabel;
    applyTriggerAccentStyles(injectedTrigger, options);
    injectedTrigger.addEventListener('click', open);
    const host = resolveTriggerHost(options);
    host.appendChild(injectedTrigger);
  }

  return {
    open,
    close,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (isOpen) close();
      if (detachResize) {
        detachResize();
        detachResize = null;
      }
      if (detachCheckoutFrame) {
        detachCheckoutFrame();
        detachCheckoutFrame = null;
      }
      if (dispatcher) {
        dispatcher.destroy();
        dispatcher = null;
      }
      if (externalTrigger) {
        externalTrigger.el.removeEventListener('click', externalTrigger.handler);
        externalTrigger = null;
      }
      if (injectedTrigger && injectedTrigger.parentNode) {
        injectedTrigger.parentNode.removeChild(injectedTrigger);
      }
      injectedTrigger = null;
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      iframe = null;
    },
  };
}
